import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { FieldTarget } from '@/lib/maps/hit-test';
import type { MapPoint } from '@/lib/maps/types';

import { checkPlacement } from './check-placement';
import {
  DUPLICATE_ROW_MESSAGE,
  EMPTY_ROW_COUNT,
  duplicateRowIndexes,
  emptyRow,
  parseCoordinate,
  rangeMessage,
  rowsFromPoints,
  rowsToPoints,
  sanitizeCoordinate as sanitize,
  type CoordinateAxis,
  type CoordinateRow,
  type RowErrors,
} from './coordinates';
import { useValidateBoundary } from './hooks';
import type { FieldOrigin } from './schemas';
import { MAX_POINTS_BY_ORIGIN } from './use-field-points';

/**
 * The **draft**, once the Grower has touched it. `null` means clean — and that is the whole design:
 * a clean panel is DERIVED from the committed shape, so marking a **Point** on the canvas, deleting
 * one, dragging one or pressing Undo all show up in the rows for free, while a draft in progress is
 * never overwritten under the Grower's hands.
 */
type Draft = { rows: CoordinateRow[]; errors: RowErrors[] };

export type CoordinateDraftOptions = {
  /** The **committed shape** — what the canvas draws. Read on every render, unlike
   *  `useFieldPoints`' `initialPoints`: a clean draft is supposed to follow it. */
  points: readonly MapPoint[];
  /** The optimistic pre-filter's targets, exactly as `useFieldPoints` takes them. */
  existingFields: readonly FieldTarget[];
  /** `null` when creating, the Field's id when editing. Required, never defaulted — see
   *  `checkPlacement`. */
  excludeFieldId: string | null;
  /** `useFieldPoints().commitPoints` — the one way a valid draft becomes the committed shape, and
   *  one undo entry. Injected rather than reached for, so this hook needs no map state of its own. */
  commitPoints: (points: readonly MapPoint[]) => Promise<boolean>;
  /** The session's origin, and therefore the row ceiling — the SAME option `useFieldPoints` takes,
   *  for the same reason: this hook holds the second half of the allowance (`canAddRow` and
   *  `addRow`'s own guard), so a cap keyed in one hook and not the other would let the panel type
   *  past what the canvas will mark. */
  origin?: FieldOrigin;
};

const noErrors = (length: number): RowErrors[] => Array.from({ length }, () => ({}));

/**
 * The Field Mapping panel's **draft coordinates** (113601).
 *
 * Three levels of commit exist on this screen (ADR-0018, grilling decision 8) and this hook owns the
 * first: rows that may be empty or invalid, that are **not** in the undo history, and that leave the
 * committed shape alone until the panel's own `Save`.
 */
export function useCoordinateDraft({
  points,
  existingFields,
  excludeFieldId,
  commitPoints,
  origin = 'drawn',
}: CoordinateDraftOptions) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const { mutateAsync } = useValidateBoundary();

  const committedRows = useMemo(() => rowsFromPoints(points), [points]);
  // Mirrors `committedRows` after every render (the same "latest ref" idiom `useDebouncedCallback`
  // uses for `callbackRef`), so `edit`'s fallback below can read the LIVE committed shape even from
  // a stale closure — e.g. a `validateCell` round trip started before the canvas changed underneath
  // it. A ref write belongs in an effect, not the render body itself (`react-hooks/refs`).
  //
  // **`useLayoutEffect`, not `useEffect`**: a passive effect is scheduled after commit, not
  // synchronous with it, so a round trip that resolves in that gap would still read a stale ref. The
  // layout effect runs in the same commit-phase call stack, before control returns to the event loop
  // — so by the time any pending promise's `.then` can run, the mirror is already current.
  const committedRowsRef = useRef(committedRows);
  useLayoutEffect(() => {
    committedRowsRef.current = committedRows;
  });
  const rows = draft?.rows ?? committedRows;
  const stored = useMemo(() => draft?.errors ?? noErrors(rows.length), [draft, rows.length]);

  /**
   * A repeated pair is **derived**, never stored, so correcting either half clears the flag as the
   * Grower types — the same criterion the stored errors get from `setValue`, for free.
   *
   * It lands on the row's **Lat** input: the sentence is about the pair, and one input carrying it
   * needs no third error slot invented for a state the designs never covered (grilling BA-4).
   */
  const errors = useMemo(() => {
    const duplicates = duplicateRowIndexes(rows);
    if (duplicates.size === 0) return stored;
    return stored.map((row, index) =>
      duplicates.has(index) ? { ...row, lat: row.lat ?? DUPLICATE_ROW_MESSAGE } : row,
    );
  }, [rows, stored]);

  /** Every mutation goes through here, so a draft can only ever be born by copying the committed
   *  rows — there is no path that starts from an empty array and loses the Grower's shape. Reads
   *  `committedRowsRef` rather than the closed-over `committedRows`, so a caller holding a stale
   *  `edit` closure (e.g. `validateCell`'s async continuation, invoked after the canvas changed
   *  mid-round-trip) still seeds a clean draft from the CURRENT committed shape, not the one that
   *  was current when the closure was created. That also makes `edit` itself stable. */
  const edit = useCallback(
    (change: (draft: Draft) => Draft) =>
      setDraft((current) =>
        change(
          current ?? {
            rows: committedRowsRef.current,
            errors: noErrors(committedRowsRef.current.length),
          },
        ),
      ),
    [],
  );

  const setValue = useCallback(
    (index: number, axis: CoordinateAxis, raw: string) =>
      edit(({ rows: current, errors: currentErrors }) => ({
        rows: current.with(index, { ...current[index]!, [axis]: sanitize(raw) }),
        // Typing is progress: the cell's own error goes, and only that cell's.
        errors: currentErrors.with(index, { ...currentErrors[index], [axis]: undefined }),
      })),
    [edit],
  );

  /**
   * Blur. Two answers, in this order:
   *
   * 1. **Range** — a pure rule, answered without a round trip. An **empty** cell is deliberately
   *    left alone: blank is a *save-time* error (113601), and flagging a cell the Grower has not
   *    reached would fire an error on their way past it.
   * 2. **Placement** — one round trip for the row's pair, and only once BOTH halves parse. There is
   *    nothing to ask about half a coordinate.
   *
   * The refusal lands on the axis just blurred. The frames show either half carrying a placement
   * message (`594:532922`), and telling the Grower at the field they left is the honest reading of a
   * verdict that is really about the pair.
   *
   * A stale reply cannot overwrite a fresh error: the result is dropped inside the updater if the
   * row's pair has changed since the request went out.
   */
  const validateCell = useCallback(
    async (index: number, axis: CoordinateAxis) => {
      const row = rows[index];
      if (!row) return;

      const value = parseCoordinate(row[axis], axis);
      if (row[axis].trim() !== '' && value === null) {
        edit(({ rows: current, errors: currentErrors }) => ({
          rows: current,
          errors: currentErrors.with(index, {
            ...currentErrors[index],
            [axis]: rangeMessage(axis),
          }),
        }));
        return;
      }

      const lat = parseCoordinate(row.lat, 'lat');
      const lon = parseCoordinate(row.lon, 'lon');
      if (lat === null || lon === null) return;

      const check = await checkPlacement(
        mutateAsync,
        { lat, lon },
        [{ lat, lon }],
        existingFields,
        excludeFieldId,
      );
      edit(({ rows: current, errors: currentErrors }) => {
        // A stale reply must not overwrite a fresher error: if the pair moved on while the request
        // was out, that verdict is about a coordinate the Grower no longer holds.
        const now = current[index];
        if (now?.lat !== row.lat || now?.lon !== row.lon) {
          return { rows: current, errors: currentErrors };
        }
        return {
          rows: current,
          // An ACCEPTED pair clears **both** axes, not only the blurred one. Both halves are in
          // range by the time execution reaches here — a sibling out of range returned at "pair
          // incomplete" above — so the only thing either can hold is a placement message from an
          // earlier blur, and leaving the sibling's would keep a refusal on screen that no longer
          // applies. (The admin twin's `clearPairError`, and it is needed.)
          errors: currentErrors.with(
            index,
            check.ok ? {} : { ...currentErrors[index], [axis]: check.inlineMessage },
          ),
        };
      });
    },
    [rows, edit, mutateAsync, existingFields, excludeFieldId],
  );

  const canAddRow = rows.length < MAX_POINTS_BY_ORIGIN[origin];
  /** D7: at exactly the minimum the control is **absent**, not greyed (grilling Q7). Derived from
   *  the count, so it cannot drift from the rows. */
  const canDeleteRow = rows.length > EMPTY_ROW_COUNT;

  const addRow = useCallback(
    () =>
      edit(({ rows: current, errors: currentErrors }) =>
        current.length < MAX_POINTS_BY_ORIGIN[origin]
          ? { rows: [...current, emptyRow()], errors: [...currentErrors, {}] }
          : { rows: current, errors: currentErrors },
      ),
    [edit, origin],
  );

  /** `toSpliced` on both arrays is the whole of "renumbers the rows below": the number a row wears
   *  IS its index, so closing the gap renumbers. Same reasoning as `deletePoint`. */
  const deleteRow = useCallback(
    (index: number) =>
      edit(({ rows: current, errors: currentErrors }) =>
        current.length > EMPTY_ROW_COUNT
          ? { rows: current.toSpliced(index, 1), errors: currentErrors.toSpliced(index, 1) }
          : { rows: current, errors: currentErrors },
      ),
    [edit],
  );

  const canSave = useMemo(
    () =>
      rowsToPoints(rows) !== null &&
      errors.every((row) => row.lat === undefined && row.lon === undefined),
    [rows, errors],
  );

  /**
   * The panel's `Save`, pressed — enabled or not, because the control is `aria-disabled` and has to
   * be able to SAY that a blank is not valid (113601). An empty cell gets the very message an
   * out-of-range one gets, which is what the criterion asks for in as many words.
   */
  const attemptSave = useCallback(async () => {
    const blanks = rows.map((row): RowErrors => {
      const flagged: RowErrors = {};
      if (row.lat.trim() === '') flagged.lat = rangeMessage('lat');
      if (row.lon.trim() === '') flagged.lon = rangeMessage('lon');
      return flagged;
    });
    if (blanks.some((row) => row.lat || row.lon)) {
      edit(({ rows: current, errors: currentErrors }) => ({
        rows: current,
        errors: currentErrors.map((row, index) => ({ ...row, ...blanks[index] })),
      }));
      return;
    }

    if (!canSave) return;
    const next = rowsToPoints(rows);
    if (!next) return;
    // Only a COMMITTED shape retires the draft. A refused ring leaves every row where it was.
    if (await commitPoints(next)) setDraft(null);
  }, [rows, canSave, edit, commitPoints]);

  /** Cancel — confirmed by the caller, which owns the dialog. Back to `null` is back to mirroring
   *  the committed shape, which is exactly "the panel reverts with the shape unchanged". */
  const cancel = useCallback(() => setDraft(null), []);

  return {
    rows,
    errors,
    dirty: draft !== null,
    setValue,
    validateCell,
    addRow,
    deleteRow,
    canAddRow,
    canDeleteRow,
    canSave,
    attemptSave,
    cancel,
  };
}
